/** A view displaying text with Readlang's click-to-translate functionality */
import * as preact from "preact";
import * as _ from "underscore";

import models from "models/models";
import readlangClient from "readlangClient";
import * as spanUtils from "spanUtils";

interface Props {
  text: string;
  sourceLanguage: string;
  targetLanguage: string;
  afterUpdate?: () => void;
  source: object; // to be added to the saved context. e.g. {bookID: ...} or {userChatID: ...}
}

interface Part {
  text: string;
  translatable: boolean;

  translating?: boolean;
  translation?: string;
}

interface RenderPart {
  text: string;
  translation?: string;
  groupID?: number;
  partIndex: number;
  translatable: boolean;
  translating: boolean;
}

interface State {
  parts: Part[];
  groups: {
    [groupID: string]: {
      from: number;
      to: number;
      translation?: string;
    };
  };
  drag?: {
    start: number;
    end: number;
  };
  groupCounter: number;
  savedUserWords: {
    from: number;
    to: number;
    userWordID: string;
  }[];
}

class TranslatableTextView extends preact.Component<Props, State> {
  textareaRef: any; // preact.RefObject<HTMLTextAreaElement>;

  constructor(props: Props) {
    super(props);

    this.state = {
      parts: splitTextIntoParts(props.text),
      groupCounter: 1,
      groups: {},
      savedUserWords: [],
    };

    // Set up document handlers
    document.addEventListener("mousemove", this.onDocumentMouseMove);
    document.addEventListener("mouseup", this.onDocumentMouseUp);

    document.addEventListener("touchmove", this.onDocumentTouchMove);
    document.addEventListener("touchend", this.onDocumentMouseUp);
  }

  onDocumentMouseMove = () => {
    if (!this.state.drag) {
      return;
    }

    // Cancel end position
    this.setState({
      drag: { start: this.state.drag.start, end: this.state.drag.start },
    });
  };

  onDocumentMouseUp = () => {
    // console.log("doc mouse/touch up");
    if (!this.state.drag) {
      return;
    }

    // Cancel
    this.setState({ drag: undefined });
  };

  onSpanMouseDown = (part) => {
    return (event) => {
      event.preventDefault();
      event.stopPropagation();

      this.setState({
        drag: { start: part.partIndex, end: part.partIndex },
      });
    };
  };

  onPointerMove({
    position,
    groupID,
  }: {
    position?: number;
    groupID?: number;
  }) {
    if (!this.state.drag) {
      return;
    }

    if (groupID !== undefined) {
      const group = this.state.groups[groupID];
      if (group.to > this.state.drag.start) {
        this.setState({
          drag: { start: this.state.drag.start, end: group.to },
        });
      } else {
        this.setState({
          drag: { start: this.state.drag.start, end: group.from },
        });
      }
    } else if (position !== undefined) {
      this.setState({
        drag: { start: this.state.drag.start, end: position },
      });
    } else {
      // reset drag
      this.setState({
        drag: {
          start: this.state.drag.start,
          end: this.state.drag.start,
        },
      });
    }
  }

  onSpanMouseMove = (part: RenderPart) => (event) => {
    if (!this.state.drag) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    this.onPointerMove({ position: part.partIndex, groupID: part.groupID });
  };

  onDocumentTouchMove = (event) => {
    if (!this.state.drag) {
      return;
    }

    if (event.handled) {
      return;
    }
    event.handled = true;

    const pageX = event.changedTouches[0].pageX;
    const pageY = event.changedTouches[0].pageY;

    if (event.touches.length > 1) {
      // cancel
      this.setState({ drag: undefined });
      return;
    }

    const x = pageX - $(document).scrollLeft();
    const y = pageY - $(document).scrollTop();

    const span = $(document.elementFromPoint(x, y)).closest(
      "span[data-p], span[data-group]"
    );

    const groupID = parseInt(span?.attr("data-group"), 10);

    if (!isNaN(groupID)) {
      this.onPointerMove({ groupID });
      return;
    }

    const position = parseInt(span?.attr("data-p"), 10);
    this.onPointerMove({ position });
  };

  onMouseUp = (positionToGroupID) => () => {
    if (!this.state.drag) {
      return;
    }

    // Check if the selection exactly matches an existing group or translating
    // part, and if so, untranslate it
    const startRenderGroupID = positionToGroupID[this.state.drag.start];
    const endRenderGroupID = positionToGroupID[this.state.drag.end];

    if (
      this.state.drag.start === this.state.drag.end &&
      this.state.parts[this.state.drag.start].translating
    ) {
      // Untranslate part
      this.setState({
        parts: [
          ...this.state.parts.slice(0, this.state.drag.start),
          {
            ...this.state.parts[this.state.drag.start],
            translating: undefined,
            translation: undefined,
          },
          ...this.state.parts.slice(this.state.drag.start + 1),
        ],
        drag: undefined,
      });
      return;
    }
    if (
      startRenderGroupID !== undefined &&
      startRenderGroupID === endRenderGroupID
    ) {
      // Untranslate group
      const groups = { ...this.state.groups };
      delete groups[startRenderGroupID];

      this.setState({
        groups,
        drag: undefined,
      });
      return;
    }

    this.translateSelection();
  };

  render() {
    /** A mix of parts and groups */
    let renderParts: RenderPart[] = [];

    const positionToGroupID: number[] = this.state.parts.map(() => undefined);

    for (const rawGroupID of Object.keys(this.state.groups)) {
      const groupID = parseInt(rawGroupID, 10);
      const group = this.state.groups[groupID];
      if (!group) {
        continue;
      }
      for (let index = group.from; index <= group.to; index++) {
        positionToGroupID[index] = groupID;
      }
    }

    let partIndex = 0;

    while (partIndex < this.state.parts.length) {
      const groupID = positionToGroupID[partIndex];

      if (groupID) {
        const lastRenderPart = _.last(renderParts);
        if (lastRenderPart?.groupID === groupID) {
          lastRenderPart.text += this.state.parts[partIndex].text;
        } else {
          renderParts.push({
            groupID,
            partIndex: partIndex,
            text: this.state.parts[partIndex].text,
            translation: this.state.groups[groupID].translation,
            translatable: true,
            translating: true,
          });
        }
      } else {
        renderParts.push({
          partIndex: partIndex,
          text: this.state.parts[partIndex].text,
          translation: this.state.parts[partIndex].translation,
          translatable: this.state.parts[partIndex].translatable,
          translating: this.state.parts[partIndex].translating,
        });
      }

      partIndex += 1;
    }

    return (
      <span>
        {renderParts.map((renderPart) =>
          renderPart.translatable ? (
            <span
              data-p={
                renderPart.groupID === undefined
                  ? renderPart.partIndex
                  : undefined
              }
              data-group={renderPart.groupID}
              class={[
                this.state.drag &&
                renderPart.partIndex >=
                  Math.min(this.state.drag.start, this.state.drag.end) &&
                renderPart.partIndex <=
                  Math.max(this.state.drag.start, this.state.drag.end)
                  ? "selecting"
                  : "",
                renderPart.translating || renderPart.groupID !== undefined
                  ? "translating"
                  : "",
                renderPart.translation ? "translated" : "",
              ].join(" ")}
              onMouseDown={this.onSpanMouseDown(renderPart)}
              onTouchStart={this.onSpanMouseDown(renderPart)}
              onMouseMove={this.onSpanMouseMove(renderPart)}
              onMouseUp={this.onMouseUp(positionToGroupID)}
              onTouchEnd={this.onMouseUp(positionToGroupID)}
            >
              {renderPart.translation ? (
                <>
                  <div class="translation">{renderPart.translation}</div>
                  <div class="original">{renderPart.text}</div>
                </>
              ) : (
                renderPart.text
              )}
            </span>
          ) : (
            <>{renderPart.text}</>
          )
        )}
      </span>
    );
  }

  async translateSelection() {
    const from = Math.min(this.state.drag.start, this.state.drag.end);
    const to = Math.max(this.state.drag.start, this.state.drag.end);

    const word = this.state.parts
      .slice(from, to + 1)
      .map((part) => part.text)
      .join("");

    // Just use the whole thing for context for now
    const context = this.props.text;

    let thisGroupID;

    if (from === to) {
      // One word
      this.setState({
        parts: [
          ...this.state.parts.slice(0, from),
          {
            ...this.state.parts[from],
            translating: true,
          },
          ...this.state.parts.slice(from + 1),
        ],
        drag: undefined,
      });
    } else {
      // Remove any existing groups
      const groups = { ...this.state.groups };
      for (const key of Object.keys(groups)) {
        if (groups[key].from >= from && groups[key].to <= to) {
          delete groups[key];
        }
      }

      // Remove any saved user words that are within this range now that we're going to extend
      // the selected range
      this.deleteSavedUserWords(from, to);

      // Create group
      thisGroupID = this.state.groupCounter;
      this.setState({
        groups: {
          ...groups,
          [thisGroupID]: {
            from,
            to,
          },
        },
        groupCounter: this.state.groupCounter + 1,
      });
    }

    const translation = (
      await readlangClient.request("GET", "/api/translate", {
        source: this.props.sourceLanguage,
        target: this.props.targetLanguage,
        q: word,
        context,
      })
    ).data.translations[0];

    if (from === to) {
      this.setState({
        parts: [
          ...this.state.parts.slice(0, from),
          {
            ...this.state.parts[from],
            translating: true,
            translation,
          },
          ...this.state.parts.slice(from + 1),
        ],
        drag: undefined,
      });
    } else {
      // handle group
      this.setState({
        groups: {
          ...this.state.groups,
          [thisGroupID]: {
            from,
            to,
            translation,
          },
        },
      });
    }

    this.saveUserWord(from, to, word, translation, context);
  }

  async deleteSavedUserWords(from: number, to: number) {
    console.log("delete saved user words", from, to);
    const savedUserWords = new Set(
      this.state.savedUserWords
        .filter((userWord) => userWord.from >= from && userWord.to <= to)
        .map((userWord) => userWord.userWordID)
    );

    console.log("delete: ", savedUserWords);

    for (const userWordID of savedUserWords) {
      await readlangClient.request("DELETE", `/api/userWord${userWordID}`, {});
    }

    this.setState({
      savedUserWords: this.state.savedUserWords.filter(
        (userWord) => !savedUserWords.has(userWord.userWordID)
      ),
    });
  }

  async saveUserWord(
    from: number,
    to: number,
    word: string,
    translation: string,
    context: string
  ) {
    const responseData = (
      await readlangClient.request("POST", "/api/userWord", {
        word,
        translation,
        language: this.props.sourceLanguage,
        contexts: [
          {
            text: context,
            ...this.props.source,
          },
        ],
      })
    ).data;

    const userWordID = responseData.upserted ?? responseData.updated;

    this.setState({
      savedUserWords: [
        ...this.state.savedUserWords,
        {
          from,
          to,
          userWordID,
        },
      ],
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State): void {
    if (
      this.props.afterUpdate &&
      (this.props.text !== prevProps.text ||
        !_.isEqual(this.state.parts, prevState.parts) ||
        !_.isEqual(this.state.groups, prevState.groups))
    ) {
      this.props.afterUpdate();
    }
  }

  componentWillUnmount(): void {
    document.removeEventListener("mousemove", this.onDocumentMouseMove);
    document.removeEventListener("mouseup", this.onDocumentMouseUp);

    document.removeEventListener("touchmove", this.onDocumentTouchMove);
    document.removeEventListener("touchend", this.onDocumentMouseUp);
  }
}

const splitTextIntoParts = (text: string) => {
  const parts: { text: string; translatable: boolean }[] = [];
  const regex = spanUtils.wordRegExpWithPunctuation();
  let lastIndex = 0;

  let match;
  while ((match = regex.exec(text)) !== null) {
    const matchIndex = match.index;

    // Add non-matching text before this match
    if (matchIndex > lastIndex) {
      parts.push({
        text: text.substring(lastIndex, matchIndex),
        translatable: false,
      });
    }

    // Add the matching word/punctuation
    parts.push({
      text: match[0],
      translatable: true,
    });

    lastIndex = matchIndex + match[0].length;
  }

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    parts.push({
      text: text.substring(lastIndex),
      translatable: false,
    });
  }

  return parts;
};

export default TranslatableTextView;
